import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { MemoizedAddOnPromoLegend as AddOnPromoLegend } from "./AddOnPromoLegend";
import { IChnlLineupThemeUI } from "@/ChnlListingPageV2/server/themeConfig";
import { IIndicatorDataset } from "@/ChnlListingPageV2/chnlListingPage.props";

export interface SearchResultsMessageProps {
  /** Root element id, and the prefix used to derive the legend items' ids. */
  id: string;
  /** CMS-authored icon legend copy; every field defends its own fallback (see
   * AddOnPromoLegend), so this is honestly optional — not every caller authors it. */
  addOnPromoLogoInfo?: IIndicatorDataset;
  theming?: IChnlLineupThemeUI["packagesTab"]["sectionHeaders"];
  /** [WAT-11717] Per-item legend visibility — a message only makes sense when a
   * channel carrying that indicator is actually present among the current search
   * results. All default to `true` so a caller that doesn't pass them (there are
   * none left, but this keeps the prop non-breaking) keeps the legend's original
   * always-show-all-three behavior. */
  showIncludedLegend?: boolean;
  showAddOnLegend?: boolean;
  showPromoLegend?: boolean;
}

/**
 * @description [WAT-11256] AC2 — search-specific messaging shown above the channel
 * grid while a search is active: it renders as the first child of the search
 * branch's full-width table cell (TableBody), immediately before the SVOD/channel
 * rows it precedes. Replaces the per-category "Add-on channels" legend (which only
 * showed on the AddOn accordion header) since search collapses every category into
 * a single consolidated list with no accordion headers at all.
 *
 * Deliberately decorative (no `role`/`aria-live`). [WAT-11717] This is NO LONGER
 * because the content is static — it isn't: the show*Legend props below add and
 * remove legend items between searches while this stays mounted. The reason that
 * survives is ownership: announcing a search result is the grid's job, and the grid
 * mounts a single persistent live region OUTSIDE this component (PackageCmprGrid.tsx)
 * that already exists before the first search mutation a screen reader needs to
 * observe. A second live region here would announce the legend alongside it, not
 * instead of it.
 */
const SearchResultsMessage = ({
  id,
  addOnPromoLogoInfo,
  theming,
  showIncludedLegend = true,
  showAddOnLegend = true,
  showPromoLegend = true
}: SearchResultsMessageProps) => (
  <DtvBox
    id={id}
    sx={{
      width: "100%",
      // DtvTheme.spacing(n) = 4n px, so this resolves to 12px vertical / 16px
      // horizontal — on the repo's 4px spacing scale, unlike a raw pixel value.
      padding: `${DtvTheme.spacing(3)} ${DtvTheme.spacing(4)}`,
      backgroundColor: theming?.background,
      color: theming?.textColor
    }}
  >
    {/* h2: defaults to the first-level child under the page heading, which itself
        defaults to h1 (ChnlListingPage.tsx: component={heading?.component || "h1"})
        — but that default is CMS-overridable, so this is an authoring constraint
        this component cannot enforce, not a guaranteed invariant. */}
    <DtvText
      content="Search results"
      component="h2"
      variant="body2"
      sx={{
        fontWeight: 600,
        marginBottom: "8px",
        color: theming?.textColor
      }}
    />
    <AddOnPromoLegend
      id={`${id}-legend`}
      addOnPromoLogoInfo={addOnPromoLogoInfo}
      textColor={theming?.textColor}
      showIncluded={showIncludedLegend}
      showAddOn={showAddOnLegend}
      showPromo={showPromoLegend}
    />
  </DtvBox>
);

export const MemoizedSearchResultsMessage = React.memo(SearchResultsMessage);
